import { useRef, type ForwardRefExoticComponent, type RefAttributes } from 'react'
import {
  CalendarDaysIcon,
  PenToolIcon,
  FilePenLineIcon,
  ClipboardCheckIcon,
  MessageSquareIcon,
  SettingsIcon,
} from 'lucide-animated'

interface AnimatedIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

type AnimatedIconComponent = ForwardRefExoticComponent<
  { size?: number; className?: string } & RefAttributes<AnimatedIconHandle>
>

/** Map from lucide-react icon display name / segment to lucide-animated equivalent. */
const ANIMATED_ICON_MAP: Record<string, AnimatedIconComponent> = {
  meetings: CalendarDaysIcon,
  draw: PenToolIcon,
  write: FilePenLineIcon,
  tasks: ClipboardCheckIcon,
  chat: MessageSquareIcon,
  settings: SettingsIcon,
}

/**
 * Returns hover handlers and an icon element for a navigation item.
 * Use `onMouseEnter` / `onMouseLeave` on the wrapping Link.
 */
export function useAnimatedNavIcon(segment: string, size = 16) {
  const ref = useRef<AnimatedIconHandle>(null)
  const AnimatedIcon = ANIMATED_ICON_MAP[segment]

  return {
    hasAnimated: !!AnimatedIcon,
    iconElement: AnimatedIcon ? (
      <AnimatedIcon ref={ref} size={size} className="flex-shrink-0" />
    ) : null,
    onMouseEnter: () => ref.current?.startAnimation(),
    onMouseLeave: () => ref.current?.stopAnimation(),
  }
}
